/**
 * GHL API Client
 * Frontend API calls for GoHighLevel integration
 */
import api from './api';

export const ghlApi = {
  // Opportunities (Leads)
  getOpportunities: (locationId, params = {}) =>
    api.get('/ghl/opportunities', {
      params: { location_id: locationId, ...params },
    }),

  // Contact-scoped list of opportunities
  getContactOpportunities: (locationId, contactId, params = {}) =>
    api.get('/ghl/opportunities', {
      params: { location_id: locationId, contact_id: contactId, ...params },
    }),

  getOpportunity: (opportunityId, locationId) =>
    api.get(`/ghl/opportunities/${opportunityId}`, {
      params: { location_id: locationId },
    }),

  updateOpportunity: (opportunityId, locationId, data) =>
    api.put(`/ghl/opportunities/${opportunityId}`, data, {
      params: { location_id: locationId },
    }),

  markOpportunityWon: (opportunityId, locationId) =>
    api.post(`/ghl/opportunities/${opportunityId}/mark-won`, null, {
      params: { location_id: locationId },
    }),

  // Contacts (Patients)
  getContacts: (locationId, params = {}) =>
    api.get('/ghl/contacts', {
      params: { location_id: locationId, ...params },
    }),

  // Search all contacts with auto-pagination (backend paginates GHL, returns all matches)
  searchAllContacts: (locationId, query) =>
    api.get('/ghl/contacts/search-all', {
      params: { location_id: locationId, query },
    }),

  searchContacts: (locationId, query) =>
    api.get('/ghl/contacts', {
      params: { location_id: locationId, query, limit: 10 },
    }),

  getContact: (contactId, locationId) =>
    api.get(`/ghl/contacts/${contactId}`, {
      params: { location_id: locationId },
    }),

  getOpportunityFDField: (locationId) =>
    api.get('/ghl/opportunity-fd-field', { params: { location_id: locationId } }),
  updateContact: (contactId, locationId, data) =>
    api.put(`/ghl/contacts/${contactId}`, data, {
      params: { location_id: locationId },
    }),

  // Pipelines & Custom Fields
  getPipelines: (locationId) =>
    api.get('/ghl/pipelines', {
      params: { location_id: locationId },
    }),

  getCustomFields: (locationId) =>
    api.get('/ghl/custom-fields', {
      params: { location_id: locationId },
    }),

  getCustomFieldFolders: (locationId) =>
    api.get('/ghl/custom-field-folders', {
      params: { location_id: locationId },
    }),

  getOpportunityFields: (locationId) =>
    api.get('/ghl/opportunity-fields', {
      params: { location_id: locationId },
    }),

  updateCustomFieldOptions: (locationId, fieldId, name, options) =>
    api.put(`/ghl/custom-fields/${fieldId}`, { name, options }, {
      params: { location_id: locationId },
    }).then(res => {
      // Notify all pages to refresh their dropdown data
      window.dispatchEvent(new CustomEvent('custom_fields_updated', { detail: { locationId } }));
      return res;
    }),

  // Cached Leads
  getCachedLeads: (locationId, status, page, limit) =>
    api.get('/leads', { params: { location_id: locationId, ...(status && { status }), ...(page && { page }), ...(limit && { limit }) } }),

  syncLeads: (locationId, status) =>
    api.post('/leads/sync', null, { params: { location_id: locationId, ...(status && { status }) } }),

  getLeadsSyncStatus: (locationId) =>
    api.get('/leads/sync-status', { params: { location_id: locationId } }),

  forceResetSync: (locationId) =>
    api.post('/leads/force-reset', null, { params: { location_id: locationId } }),

  clearLeadsCache: (locationId) =>
    api.post('/leads/clear-cache', null, { params: { location_id: locationId } }),

  getLeadQuickContext: (contactId, locationId, refresh = false) =>
    api.get(`/leads/quick-context/${contactId}`, { params: { location_id: locationId, ...(refresh && { refresh: true }) } }),

  // Notes & Tasks
  getUsers: (locationId) =>
    api.get('/ghl/users', {
      params: { location_id: locationId },
    }),

  updateContactTag: (locationId, payload) =>
    api.post('/ghl/contacts/tags', payload, {
      params: { location_id: locationId },
    }),

  // Calendars / Scheduling
  getCalendars: (locationId, includeInactive = false) =>
    api.get('/ghl/calendars', {
      params: { location_id: locationId, include_inactive: includeInactive },
    }),

  getCalendarSlots: (locationId, params) =>
    api.get('/ghl/calendars/slots', {
      params: { location_id: locationId, ...params },
    }),

  getCalendarEvents: (locationId, params) =>
    api.get('/ghl/calendars/events', {
      params: { location_id: locationId, ...params },
    }),

  getCalendarBulkDay: (locationId, date, timezone, refresh = false) =>
    api.get('/ghl/calendars/bulk-day', {
      params: { location_id: locationId, date, timezone, ...(refresh && { refresh: true }) },
    }),

  forceCalendarSync: (locationId, date, timezone) =>
    api.post('/ghl/calendars/force-sync', null, {
      params: { location_id: locationId, ...(date && { date }), ...(timezone && { timezone }) },
    }),

  getCrmBookedIds: (locationId, date) =>
    api.get('/ghl/calendars/crm-booked-ids', { params: { location_id: locationId, date } }),


  getContactAppointments: (locationId, contactId) =>
    api.get('/ghl/calendars/contact-appointments', {
      params: { location_id: locationId, contact_id: contactId },
    }),

  createCalendarAppointment: (locationId, data) =>
    api.post('/ghl/calendars/appointments', data, {
      params: { location_id: locationId },
    }),

  rescheduleAppointment: (eventId, locationId, data) =>
    api.put(`/ghl/calendars/appointments/${eventId}/reschedule`, data, {
      params: { location_id: locationId },
    }),

  cancelAppointment: (eventId, locationId, data = {}) =>
    api.put(`/ghl/calendars/appointments/${eventId}/cancel`, data, {
      params: { location_id: locationId },
    }),

  updateAppointment: (eventId, locationId, data) =>
    api.put(`/ghl/calendars/appointments/${eventId}`, data, {
      params: { location_id: locationId },
    }),

  updateAppointmentDirect: (eventId, locationId, data) =>
    api.put(`/ghl/calendars/appointments/${eventId}/direct`, data, {
      params: { location_id: locationId },
    }),

  getAppointmentDetail: (eventId, locationId) =>
    api.get(`/ghl/calendars/appointments/${eventId}`, {
      params: { location_id: locationId },
    }),

  getSchedulingStats: (locationId) =>
    api.get('/ghl/calendars/scheduling-stats', {
      params: { location_id: locationId },
    }),

  // Intake logs
  createIntakeLog: (locationId, data) =>
    api.post('/ghl/calendars/intake-log', data, {
      params: { location_id: locationId },
    }),
  scheduleIntakeSend: (locationId, data) =>
    api.post('/ghl/calendars/schedule-intake', data, {
      params: { location_id: locationId },
    }),

  getIntakeLog: (locationId, contactId) =>
    api.get('/ghl/calendars/intake-log', {
      params: { location_id: locationId, contact_id: contactId },
    }),

  // Contact creation
  createContact: (locationId, data, linkCallId) =>
    api.post('/ghl/contacts', data, {
      params: linkCallId ? { location_id: locationId, link_call_id: linkCallId } : { location_id: locationId },
    }),

  // Appointment types
  getAppointmentTypes: (locationId) =>
    api.get('/appointment-types', {
      params: { location_id: locationId },
    }),

  createAppointmentType: (locationId, data) =>
    api.post('/appointment-types', data, {
      params: { location_id: locationId },
    }),

  updateAppointmentType: (typeId, locationId, data) =>
    api.put(`/appointment-types/${typeId}`, data, {
      params: { location_id: locationId },
    }),

  deleteAppointmentType: (typeId, locationId) =>
    api.delete(`/appointment-types/${typeId}`, {
      params: { location_id: locationId },
    }),

  getContactNotes: (locationId, contactId) =>
    api.get('/ghl/notes', {
      params: { location_id: locationId, contact_id: contactId },
    }),

  addNote: (locationId, data) =>
    api.post('/ghl/notes', data, {
      params: { location_id: locationId },
    }),

  updateNote: (noteId, locationId, data) =>
    api.put(`/ghl/notes/${noteId}`, data, {
      params: { location_id: locationId },
    }),

  deleteNote: (noteId, locationId, contactId) =>
    api.delete(`/ghl/notes/${noteId}`, {
      params: { location_id: locationId, contact_id: contactId },
    }),

  getContactTasks: (locationId, contactId) =>
    api.get('/ghl/tasks', {
      params: { location_id: locationId, contact_id: contactId },
    }),

  createTask: (locationId, data) =>
    api.post('/ghl/tasks', data, {
      params: { location_id: locationId },
    }),

  updateTask: (taskId, locationId, data) =>
    api.put(`/ghl/tasks/${taskId}`, data, {
      params: { location_id: locationId },
    }),

  setTaskCompleted: (taskId, locationId, contactId, completed) =>
    api.put(`/ghl/tasks/${taskId}/completed`, { contact_id: contactId, completed }, {
      params: { location_id: locationId },
    }),

  // Front Desk Tasks
  getFrontDeskTasks: (locationId, refresh = false) =>
    api.get('/front-desk-tasks', { params: { location_id: locationId, refresh } }),

  syncFrontDeskTasks: (locationId, mode = 'quick') =>
    api.post('/front-desk-tasks/sync', null, { params: { location_id: locationId, mode } }),

  getSyncStatus: (locationId) =>
    api.get('/front-desk-tasks/sync-status', { params: { location_id: locationId } }),

  updateFrontDeskTask: (taskId, locationId, data) =>
    api.put(`/front-desk-tasks/${taskId}`, data, { params: { location_id: locationId } }),

  toggleFrontDeskTaskComplete: (taskId, locationId, contactId, completed) =>
    api.put(`/front-desk-tasks/${taskId}/complete`, { contact_id: contactId, completed }, { params: { location_id: locationId } }),

  invalidateFrontDeskTasksCache: (locationId) =>
    api.post('/front-desk-tasks/invalidate-cache', null, { params: { location_id: locationId } }),

  clearCallCache: (locationId) =>
    api.post('/calls/clear-cache', null, { params: { location_id: locationId } }),

  mergeDuplicateCalls: (locationId, days = 7) =>
    api.post('/calls/merge-duplicates', null, { params: { location_id: locationId, days } }),


  syncWithCrm: (locationId, dateFrom = '2026-01-01', dateTo = '', signal = null) =>
    api.post('/calls/sync-with-crm', null, { params: { location_id: locationId, date_from: dateFrom, date_to: dateTo || undefined }, signal }),

  syncWithCrmApply: (locationId, action, ids = '', dateFrom = '2026-01-01', dateTo = '') =>
    api.post('/calls/sync-with-crm/apply', null, { params: { location_id: locationId, action, ids: ids || undefined, date_from: dateFrom, date_to: dateTo || undefined } }),


  // Conversations
  getConversations: (locationId, contactId = null) =>
    api.get('/ghl/conversations', {
      params: { location_id: locationId, ...(contactId && { contact_id: contactId }) },
    }),

  getMessages: (conversationId, locationId) =>
    api.get(`/ghl/conversations/${conversationId}/messages`, {
      params: { location_id: locationId },
    }),

  // Juvonno
  getJuvonnoPatient: (locationId, customerId) =>
    api.get(`/juvonno/patient/${customerId}`, {
      params: { location_id: locationId },
    }),

  verifyJuvonnoPatient: (locationId, customerId, params) =>
    api.get(`/juvonno/verify/${customerId}`, {
      params: { location_id: locationId, ...params },
    }),

  searchJuvonnoPatients: (locationId, params) =>
    api.get('/juvonno/search', { params: { location_id: locationId, ...params } }),

  markJuvonnoVerified: (locationId, contactId, juvonnoId) =>
    api.post('/juvonno/mark-verified', null, { params: { location_id: locationId, contact_id: contactId, juvonno_id: juvonnoId } }),

  isJuvonnoVerified: (locationId, contactId) =>
    api.get('/juvonno/is-verified', { params: { location_id: locationId, contact_id: contactId } }),

  // Appointment Logs
  createAppointmentLog: (data) =>
    api.post('/appointment-logs', data),

  retryAppointment: (data) =>
    api.post('/appointment-logs/retry', data),

  getAppointmentLogs: (locationId, params = {}) =>
    api.get('/appointment-logs', {
      params: { location_id: locationId, ...params },
    }),

  exportAppointmentLogs: (locationId, params = {}) => {
    const base = api.defaults.baseURL || '';
    const queryStr = new URLSearchParams({ location_id: locationId, ...params }).toString();
    return `${base}/appointment-logs/export?${queryStr}`;
  },

  // Schedule Overrides
  getOverrides: (locationId, params = {}) =>
    api.get('/schedule-overrides', { params: { location_id: locationId, ...params } }),

  getOverridesForDate: (locationId, date) =>
    api.get('/schedule-overrides/for-date', { params: { location_id: locationId, date } }),

  createOverride: (data) =>
    api.post('/schedule-overrides', data),

  updateOverride: (overrideId, data) =>
    api.put(`/schedule-overrides/${overrideId}`, data),

  deleteOverride: (overrideId, locationId, accessCode) =>
    api.delete(`/schedule-overrides/${overrideId}`, { params: { location_id: locationId, access_code: accessCode } }),

  getAccessCodeStatus: (locationId) =>
    api.get('/schedule-overrides/access-code', { params: { location_id: locationId } }),

  setAccessCode: (data) =>
    api.put('/schedule-overrides/access-code', data),

  // Availability logs
  logAvailabilityChange: (data) =>
    api.post('/schedule-overrides/availability-log', data),

  getAvailabilityLogs: (locationId, calendarId) =>
    api.get('/schedule-overrides/availability-log', { params: { location_id: locationId, calendar_id: calendarId } }),

  deleteAvailabilityLog: (logId, locationId, accessCode, revert = false) =>
    api.delete(`/schedule-overrides/availability-log/${logId}`, { params: { location_id: locationId, access_code: accessCode, revert } }),


  // Scheduled (post-dated) availability
  getScheduledAvailability: (locationId, calendarId) =>
    api.get('/schedule-overrides/scheduled-availability', { params: { location_id: locationId, calendar_id: calendarId } }),

  createScheduledAvailability: (data) =>
    api.post('/schedule-overrides/scheduled-availability', data),

  deleteScheduledAvailability: (itemId, locationId, accessCode) =>
    api.delete(`/schedule-overrides/scheduled-availability/${itemId}`, { params: { location_id: locationId, access_code: accessCode } }),

  // Calendar Availability
  getCalendarDetails: (calendarId, locationId) =>
    api.get(`/ghl/calendars/${calendarId}/details`, { params: { location_id: locationId } }),

  updateCalendarAvailability: (calendarId, locationId, data) =>
    api.put(`/ghl/calendars/${calendarId}/availability`, data, { params: { location_id: locationId } }),

  // Calendar Offboarding
  offboardCalendar: (calendarId, locationId, data) =>
    api.put(`/ghl/calendars/${calendarId}/offboard`, data, { params: { location_id: locationId } }),

  reactivateCalendar: (calendarId, locationId, data) =>
    api.put(`/ghl/calendars/${calendarId}/reactivate`, data, { params: { location_id: locationId } }),

  scheduleOffboard: (calendarId, locationId, data) =>
    api.post(`/ghl/calendars/${calendarId}/schedule-offboard`, data, { params: { location_id: locationId } }),

  getScheduledOffboards: (calendarId, locationId) =>
    api.get(`/ghl/calendars/${calendarId}/scheduled-offboards`, { params: { location_id: locationId } }),

  deleteScheduledOffboard: (itemId, locationId, accessCode) =>
    api.delete(`/ghl/calendars/scheduled-offboard/${itemId}`, { params: { location_id: locationId, access_code: accessCode } }),

  permanentlyDeleteCalendar: (calendarId, locationId, data) =>
    api.delete(`/ghl/calendars/${calendarId}/permanent`, { params: { location_id: locationId }, data }),

  getCalendarActivityLog: (locationId, limit = 200) =>
    api.get('/ghl/calendars/activity-log', { params: { location_id: locationId, limit } }),

  // Slot Configuration (Duration / Interval)
  getSlotConfigs: (locationId, calendarId) =>
    api.get('/ghl/calendars/slot-configs', { params: { location_id: locationId, calendar_id: calendarId } }),

  getSlotConfigsForDate: (locationId, date) =>
    api.get('/ghl/calendars/slot-configs/for-date', { params: { location_id: locationId, date } }),

  createSlotConfig: (data) =>
    api.post('/ghl/calendars/slot-configs', data),

  deleteSlotConfig: (configId, locationId) =>
    api.delete(`/ghl/calendars/slot-configs/${configId}`, { params: { location_id: locationId } }),

  syncSlotConfig: (configId, locationId) =>
    api.post(`/ghl/calendars/slot-configs/${configId}/sync`, null, { params: { location_id: locationId } }),

  // Campaign Rules
  getCampaignRules: (locationId) =>
    api.get('/campaign-rules', { params: { location_id: locationId } }),
  createCampaignRule: (data) =>
    api.post('/campaign-rules', data),
  updateCampaignRule: (ruleId, data) =>
    api.put(`/campaign-rules/${ruleId}`, data),
  deleteCampaignRule: (ruleId) =>
    api.delete(`/campaign-rules/${ruleId}`),
  getCampaignAssignments: (locationId, status) =>
    api.get('/campaign-rules/assignments', { params: { location_id: locationId, ...(status && { status }) } }),
  assignCampaign: (assignmentId, campaignValue) =>
    api.put(`/campaign-rules/assignments/${assignmentId}/assign`, { campaign_value: campaignValue }),
  skipCampaignAssignment: (assignmentId) =>
    api.put(`/campaign-rules/assignments/${assignmentId}/skip`),

  // Calls
  // 45s timeout: the /calls endpoint fans out to Mongo + optional GHL
  // enrichment. Without a hard cap a stalled TCP connection could keep the
  // spinner up indefinitely (Chrome default is ~5 min). 45s is well above
  // the p99 response time (~4s) but low enough to fail fast.
  getCalls: (locationId, params = {}) =>
    api.get('/calls', {
      params: { location_id: locationId, ...params },
      timeout: 45000,
    }),

  classifyCall: (callId, locationId, data) =>
    api.post(`/calls/${callId}/classify`, data, {
      params: { location_id: locationId },
    }),

  reclassifyCall: (callId, locationId, force = false) =>
    api.post(`/calls/${callId}/reclassify`, null, {
      params: { location_id: locationId, force },
    }),

  // Instant Pull — force-refresh transcript + recording FROM CRM via
  // message_id AND run classification in one round-trip. Wired to the
  // per-row refresh button on the Call Log (Sep 2026).
  pullCallFromCrm: (callId, locationId, reclassify = true) =>
    api.post(`/calls/${callId}/pull`, null, {
      params: { location_id: locationId, reclassify },
    }),

  // ── TEMPORARY admin tool — removed in next build ──
  adminBulkReclassify: (data) =>
    api.post('/calls/admin-bulk-reclassify', data),
  adminBulkReclassifyStatus: () =>
    api.get('/calls/admin-bulk-reclassify/status'),
  adminBulkReclassifyCancel: () =>
    api.post('/calls/admin-bulk-reclassify/cancel'),

  // ── ADMIN: one-shot direction fix (recovery for the May 12 webhook bug) ──
  adminFixDirections: (locationId, days = 30) =>
    api.post('/calls/admin-fix-directions', null, { params: { location_id: locationId, days } }),
  adminFixDirectionsStatus: (locationId) =>
    api.get('/calls/admin-fix-directions/status', { params: { location_id: locationId } }),

  adminRuleStats: (days = 30, locationId = null) =>
    api.get('/calls/admin/rule-stats', { params: { days, location_id: locationId || undefined } }),

  extractContactInfo: (locationId, data) =>
    api.post('/calls/extract-contact-info', data, {
      params: { location_id: locationId },
    }),

  toggleCallStar: (callId, locationId) =>
    api.post(`/calls/${callId}/star`, null, {
      params: { location_id: locationId },
    }),

  linkCallContact: (callId, locationId, contactId) =>
    api.post(`/calls/${callId}/link-contact`, { contact_id: contactId }, {
      params: { location_id: locationId },
    }),

  getCallStats: (locationId, days = 7, from_date, to_date) => {
    const params = { location_id: locationId };
    if (from_date && to_date) { params.from_date = from_date; params.to_date = to_date; }
    else { params.days = days; }
    return api.get('/calls/stats', { params });
  },

  getTabCounts: (locationId, days = 7, from_date, to_date) => {
    const params = { location_id: locationId };
    if (from_date && to_date) { params.from_date = from_date; params.to_date = to_date; }
    else { params.days = days; }
    return api.get('/calls/tab-counts', { params });
  },

  getMissedCalls: (locationId, days = 7, from_date, to_date) => {
    const params = { location_id: locationId };
    if (from_date && to_date) { params.from_date = from_date; params.to_date = to_date; }
    else { params.days = days; }
    return api.get('/calls/missed', { params });
  },

  markMissedDone: (locationId, callId, notes = '') =>
    api.post('/calls/missed/mark-done', { call_id: callId, notes }, { params: { location_id: locationId } }),

  undoMissedDone: (locationId, callId) =>
    api.post('/calls/missed/undo', { call_id: callId }, { params: { location_id: locationId } }),

  reportCall: (locationId, data) =>
    api.post('/calls/report', data, { params: { location_id: locationId } }),

  getLiveCalls: (locationId) =>
    api.get('/webhooks/live-calls', { params: locationId ? { location_id: locationId } : {} }),

  forceCompleteLiveCall: (callId) =>
    api.delete(`/webhooks/live-calls/${callId}`),

  getCallReports: (locationId, status, issueType) =>
    api.get('/calls/reports', { params: { location_id: locationId, status, issue_type: issueType } }),

  updateCallReport: (reportId, status, notes = '') =>
    api.put(`/calls/reports/${reportId}`, null, { params: { status, notes } }),

  getAiChangelog: () =>
    api.get('/calls/ai-changelog'),

  getSanityChecks: () =>
    api.get('/calls/sanity-checks'),

  getPermanentRules: () =>
    api.get('/calls/permanent-rules'),

  addPermanentRule: (data) =>
    api.post('/calls/permanent-rules', data),

  deletePermanentRule: (ruleId) =>
    api.delete(`/calls/permanent-rules/${ruleId}`),

  getReviewQueue: (locationId, confidenceThreshold) =>
    api.get('/calls/review-queue', { params: { location_id: locationId, confidence_threshold: confidenceThreshold } }),

  confirmReviewCall: (callId, locationId) =>
    api.post(`/calls/review-queue/${callId}/confirm`, null, { params: { location_id: locationId } }),

  dismissReviewCall: (callId, locationId) =>
    api.post(`/calls/review-queue/${callId}/dismiss`, null, { params: { location_id: locationId } }),

  getRevenueSettings: (locationId) =>
    api.get('/calls/revenue-settings', { params: { location_id: locationId } }),

  updateRevenueSettings: (locationId, data) =>
    api.put('/calls/revenue-settings', data, { params: { location_id: locationId } }),

  getCallAgents: (locationId, refresh = false) =>
    api.get('/calls/agents', {
      params: { location_id: locationId, ...(refresh && { refresh: true }) },
    }),

  // Follow-ups
  getFollowUps: (locationId, params = {}) =>
    api.get('/follow-ups', { params: { location_id: locationId, ...params } }),
  createFollowUp: (locationId, data) =>
    api.post('/follow-ups', data, { params: { location_id: locationId } }),
  updateFollowUp: (id, locationId, data) =>
    api.put(`/follow-ups/${id}`, data, { params: { location_id: locationId } }),
  deleteFollowUp: (id, locationId) =>
    api.delete(`/follow-ups/${id}`, { params: { location_id: locationId } }),

  // Winback
  getWinback: (locationId, params = {}) =>
    api.get('/winback', { params: { location_id: locationId, ...params } }),
  getWinbackCalls: (winbackId, locationId) =>
    api.get(`/winback/${winbackId}/calls`, { params: { location_id: locationId } }),
  createWinback: (locationId, data) =>
    api.post('/winback', data, { params: { location_id: locationId } }),
  deleteAllWinbacks: (locationId, beforeDate = null) => {
    const params = { location_id: locationId, confirm: 'DELETE_ALL_WINBACKS' };
    if (beforeDate) params.before_date = beforeDate;
    return api.delete('/winback/all', { params });
  },
  addWinbackAttempt: (id, locationId, data) =>
    api.post(`/winback/${id}/attempt`, data, { params: { location_id: locationId } }),
  resolveWinback: (id, locationId, data) =>
    api.put(`/winback/${id}/resolve`, data, { params: { location_id: locationId } }),

  // Not-Booked Reasons
  getNotBookedReasons: (locationId) =>
    api.get('/not-booked-reasons', { params: { location_id: locationId } }),
  createNotBookedReason: (locationId, data) =>
    api.post('/not-booked-reasons', data, { params: { location_id: locationId } }),
  updateNotBookedReason: (id, locationId, data) =>
    api.put(`/not-booked-reasons/${id}`, data, { params: { location_id: locationId } }),
  deleteNotBookedReason: (id, locationId) =>
    api.delete(`/not-booked-reasons/${id}`, { params: { location_id: locationId } }),

  // Call Detail (webhook data with transcript + AI summary)
  getCallDetail: (callId, params = {}) =>
    api.get(`/webhooks/call-detail/${callId}`, { params }),

  // Webhook Logs
  getWebhookLogs: (params = {}) =>
    api.get('/webhooks/logs', { params }),
  clearWebhookLogs: (locationId) =>
    api.delete('/webhooks/logs', { params: locationId ? { location_id: locationId } : {} }),

  // LLM Config
  getLlmConfig: () =>
    api.get('/webhooks/llm-config'),
  updateLlmConfig: (data) =>
    api.put('/webhooks/llm-config', data),

  // Historical Call Import
  startHistoricalImport: (locationId, startDate = '2026-01-01', force = false) =>
    api.post('/calls/import-historical', null, {
      params: { location_id: locationId, start_date: startDate, ...(force && { force: true }) },
    }),
  getImportStatus: (locationId) =>
    api.get('/calls/import-status', { params: { location_id: locationId } }),
  cancelImport: (locationId) =>
    api.post('/calls/import-cancel', null, { params: { location_id: locationId } }),
  backfillRecordings: (locationId) =>
    api.post('/calls/backfill-recordings', null, { params: { location_id: locationId } }),
  backfillBookingTypes: (locationId) =>
    api.post('/calls/backfill-booking-type', null, { params: { location_id: locationId } }),

  // Scheduling Analytics
  getSchedulingAnalytics: (locationId, days = 30, calendarId = null) =>
    api.get('/scheduling-analytics/stats', {
      params: { location_id: locationId, days, ...(calendarId && { calendar_id: calendarId }) },
    }),

  // ICS Calendar Feeds
  getIcsUrls: (locationId) =>
    api.get('/ics/urls', { params: { location_id: locationId } }),
  setIcsUrl: (locationId, data) =>
    api.post('/ics/urls', data, { params: { location_id: locationId } }),
  deleteIcsUrl: (locationId, calendarId) =>
    api.delete('/ics/urls', { params: { location_id: locationId, calendar_id: calendarId } }),
  getIcsEvents: (locationId, date, refresh = false) =>
    api.get('/ics/events', { params: { location_id: locationId, date, ...(refresh && { refresh: true }) } }),

  // Booking metadata for analytics
  saveBookingMetadata: (locationId, data) =>
    api.post('/scheduling-analytics/booking-metadata', data, { params: { location_id: locationId } }),

  // Reporting API Key
  getReportingApiKeyStatus: (locationId) =>
    api.get('/reports/api-key/status', { params: { location_id: locationId } }),
  generateReportingApiKey: (locationId) =>
    api.post('/reports/api-key/generate', null, { params: { location_id: locationId } }),
  revokeReportingApiKey: (locationId, maskedKey) =>
    api.delete('/reports/api-key', { params: { location_id: locationId, ...(maskedKey && { masked_key: maskedKey }) } }),
  generateMasterApiKey: () =>
    api.post('/reports/api-key/generate-master'),
  revokeMasterApiKey: () =>
    api.delete('/reports/api-key', { params: { master: true } }),
  // Super-admin: reveal plaintext key. Omit locationId to reveal the master key.
  revealReportingApiKey: (locationId) =>
    api.post('/reports/api-key/reveal', null, {
      params: locationId ? { location_id: locationId } : {},
    }),
  // HQ Data
  getHqFormStats: (locationId, days = 30) =>
    api.get('/hq/forms', { params: { location_id: locationId, days } }),

  getCommunicationStats: (locationId, days = 7) =>
    api.get('/hq/communication', { params: { location_id: locationId, days } }),

  getLeadFunnel: (locationId, days = 7) =>
    api.get('/hq/lead-funnel', { params: { location_id: locationId, days } }),

  getLeadsAnalytics: (locationId, days = 30) =>
    api.get('/hq/leads-analytics', { params: { location_id: locationId, days } }),

  getClinicPulse: (locationId, days = 7) =>
    api.get('/hq/clinic-pulse', { params: { location_id: locationId, days } }),

  checkLeadConversions: (locationId) =>
    api.post('/hq/check-conversions', null, { params: { location_id: locationId } }),

  getLeadConversions: (locationId, days = 0) =>
    api.get('/hq/lead-conversions', { params: { location_id: locationId, days } }),

  getNotConvertedLeads: (locationId, days = 0) =>
    api.get('/hq/not-converted-leads', { params: { location_id: locationId, days } }),

  getConvertedLeads: (locationId, days = 0) =>
    api.get('/hq/converted-leads', { params: { location_id: locationId, days } }),

  // Google Review Requests
  createReviewRequest: (locationId, data) =>
    api.post('/review-requests', data, { params: { location_id: locationId } }),
  rescheduleReviewRequest: (appointmentId, locationId, data) =>
    api.put(`/review-requests/${appointmentId}/reschedule`, data, { params: { location_id: locationId } }),
  cancelReviewRequest: (appointmentId, locationId) =>
    api.post(`/review-requests/${appointmentId}/cancel`, null, { params: { location_id: locationId } }),
  getReviewRequests: (locationId, { status, source, appointmentType, dateFrom, dateTo, limit, offset } = {}) =>
    api.get('/review-requests', {
      params: {
        location_id: locationId,
        ...(status && { status }),
        ...(source && { source }),
        ...(appointmentType && { appointment_type: appointmentType }),
        ...(dateFrom && { date_from: dateFrom }),
        ...(dateTo && { date_to: dateTo }),
        ...(limit && { limit }),
        ...(offset != null && { offset }),
      },
    }),

  // ── Inbox ──
  getInboxConversations: (locationId, { status, search, msgType, limit, afterId, startAfterDate, contactId } = {}) =>
    api.get('/inbox/conversations', {
      params: {
        location_id: locationId,
        ...(status && { status }),
        ...(search && { search }),
        ...(msgType && { msg_type: msgType }),
        ...(limit && { limit }),
        ...(startAfterDate && { start_after_date: startAfterDate }),
        ...(afterId && !startAfterDate && { after_id: afterId }),
        ...(contactId && { contact_id: contactId }),
      },
    }),
  getInboxMessages: (conversationId, locationId, limit = 50) =>
    api.get(`/inbox/conversations/${conversationId}/messages`, {
      params: { location_id: locationId, limit },
    }),
  sendInboxMessage: (locationId, data) =>
    api.post('/inbox/conversations/send', data, { params: { location_id: locationId } }),
  uploadInboxAttachment: (locationId, contactId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/inbox/conversations/upload', formData, {
      params: { location_id: locationId, contact_id: contactId },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  suggestReply: (data) =>
    api.post('/inbox/suggest-reply', data),
  markInboxRead: (conversationId, locationId) =>
    api.put(`/inbox/conversations/${conversationId}/read`, null, { params: { location_id: locationId } }),
  clearInboxCache: (locationId) =>
    api.delete('/inbox/cache', { params: { location_id: locationId } }),
  // iter199 — On-demand incremental GHL→Mongo sync for one location.
  // Awaited by the Refresh button so users see freshly-pulled data instead
  // of the stale Mongo mirror (which lags GHL by up to 60 s otherwise).
  inboxSyncNow: (locationId, { full = false } = {}) =>
    api.post('/inbox/sync-now', null, { params: { location_id: locationId, full } }),
  // iter204 — Force-refetch a single conversation's messages from GHL
  // (including call meta, recording URLs, email attachments, activity
  // entries). Used by the full-refresh button on the contact bar.
  inboxRefreshConversation: (conversationId, locationId, { limit = 500 } = {}) =>
    api.post(`/inbox/conversations/${conversationId}/refresh`, null,
      { params: { location_id: locationId, limit } }),
  // iter204 — Admin call-meta backfill (super-admin only). One-time
  // per-location remediation after deploy to populate call meta on
  // historical calls stored pre-iter203.
  inboxBackfillCallMeta: (locationId) =>
    api.post('/inbox/admin/backfill-call-meta', null, { params: { location_id: locationId } }),
  inboxBackfillCallMetaStatus: (locationId) =>
    api.get('/inbox/admin/backfill-call-meta/status', { params: { location_id: locationId } }),
  markInboxUnread: (conversationId, locationId) =>
    api.put(`/inbox/conversations/${conversationId}/unread`, null, { params: { location_id: locationId } }),
  toggleInboxStar: (conversationId, starred, locationId) =>
    api.put(`/inbox/conversations/${conversationId}/star`, null, { params: { starred, location_id: locationId } }),
  getInboxContact: (contactId, locationId) =>
    api.get(`/inbox/contact/${contactId}`, { params: { location_id: locationId } }),
  getMessageTranscript: (messageId, locationId) =>
    api.get(`/inbox/messages/${messageId}/transcript`, { params: { location_id: locationId } }),
  getMessageRecording: (messageId, locationId) =>
    api.get(`/inbox/messages/${messageId}/recording`, { params: { location_id: locationId } }),
  getEmailHtml: (messageId, locationId, { altId, emailMessageId } = {}) => {
    const id = emailMessageId || messageId;
    const params = { location_id: locationId };
    if (altId) params.alt_id = altId;
    return api.get(`/inbox/messages/${id}/email`, { params });
  },
  pullMessageFromCrm: (messageId, locationId) =>
    api.post(`/inbox/messages/${messageId}/pull`, null, { params: { location_id: locationId } }),

  // Intake & Consent
  getContactFormSubmissions: (locationId, contactId) =>
    api.get(`/ghl/contact/${contactId}/form-submissions`, { params: { location_id: locationId } }),

  getContactDocuments: (locationId, contactId) =>
    api.get(`/ghl/contact/${contactId}/documents`, { params: { location_id: locationId } }),

  // AI Insights
  getAiInsights: (data) =>
    api.post('/inbox/ai-insights', data),

  // Activity Timeline — DB-cached aggregate of notes + appointments + tasks
  getContactActivityTimeline: (locationId, contactId, { refresh = false } = {}) =>
    api.get(`/inbox/contact/${contactId}/activity`, {
      params: { location_id: locationId, ...(refresh ? { refresh: true } : {}) },
    }),
};

export default ghlApi;
